import { useMemo } from "react";

import Box from "@mui/material/Box";
import DialogContent from "@mui/material/DialogContent";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Link, Outlet, useLocation, useSearchParams } from "react-router-dom";

import LoadingOrValue from "@covertree/ui/loading/LoadingOrValue";
import ErrorContent from "@covertree/ui/content/ErrorContent";
import { InsuranceAdoptionStatus, Resident } from "@graphql/api";
import { CognitoUserGroup } from "@enums";
import { useAuthContext } from "@covertree/auth";
import useAppDispatch from "@redux/hooks/useAppDispatch";
import { resetPolicyTypeForm } from "@redux/coverages/coveragesSlice";

import ResidentDetailsTabs from "./ResidentDetailsTabs";
import DetailsDrawerTop from "../../DetailsDrawerTop";
import {
  BASE_PROOF_OF_COVERAGE_PATH,
  PROOF_OF_COVERAGE_ROUTES,
} from "@routing/Residents/ProofOfCoverage";

interface Props {
  isError?: boolean;
  isLoading?: boolean;
  onClose(): void;
  resident?: Resident;
}

export default function ResidentDetailsContent({
  isError,
  isLoading,
  onClose,
  resident,
}: Props) {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up("xl"));

  const [searchParams] = useSearchParams();

  const { hasGroup } = useAuthContext();

  const canAddProofOfCoverage = useMemo(
    () =>
      resident &&
      resident?.insuranceAdoption?.status !==
        InsuranceAdoptionStatus.Excluded &&
      hasGroup([
        CognitoUserGroup.CoverTreeEmployee,
        CognitoUserGroup.CoverTreeExecutive,
      ]),
    [resident, hasGroup]
  );

  const handleOpen = () => {
    dispatch(resetPolicyTypeForm());
  };

  return (
    <>
      <Stack direction="column" pt={3} height="100%">
        <Box px={3}>
          <DetailsDrawerTop
            showResident={!isError}
            onClose={onClose}
            isLoading={isLoading}
            resident={resident}
            mb={4}
          />
          {!isError && (
            <Box
              sx={{
                display: "flex",
                direction: "row",
                justifyContent: "space-between",
              }}>
              <Typography variant="h3" mb={4}>
                <LoadingOrValue
                  loading={!resident && isLoading}
                  value={`${resident?.firstName} ${resident?.lastName}${
                    resident?.residentId && `, ${resident.residentId}`
                  }`}
                  height={40}
                  width={340}
                />
              </Typography>
              {canAddProofOfCoverage && (
                <Button
                  component={Link}
                  to={`${location.pathname}/${BASE_PROOF_OF_COVERAGE_PATH}/${PROOF_OF_COVERAGE_ROUTES.POLICY_TYPE}?${searchParams.toString()}`}
                  size={isDesktop ? "small" : "medium"}
                  onClick={handleOpen}>
                  <Typography sx={isDesktop ? { whiteSpace: "nowrap" } : {}}>
                    Add New Proof of Coverage
                  </Typography>
                </Button>
              )}
            </Box>
          )}
        </Box>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            paddingTop: 0,
            flex: 1,
          }}>
          {!isError && (
            <ResidentDetailsTabs resident={resident} isLoading={isLoading} />
          )}
          {isError && (
            <ErrorContent>
              Oops! <br />
              Something went wrong while loading resident details data. <br />
              Please try again later.
            </ErrorContent>
          )}
        </DialogContent>
      </Stack>
      <Outlet />
    </>
  );
}
